import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  Mail, Lock, Eye, EyeOff, AlertCircle, Check
} from 'lucide-react';
import { motion } from 'framer-motion';
import logoGWPC from '../../assets/logo/gwpc-logo-transparent.png';

/* Fresh Odoo-style login screen. The previous version was a two-column
   marketing layout (Bahrain flag photo, sunset gradient, feature list) with
   separate desktop/mobile branches. This replaces it with the same clean,
   minimal, centered-card look used by the rest of the app's Odoo chrome
   (theme.css tokens: --bg, --surface, --line, --accent, etc.) -- a single
   responsive layout instead of two parallel ones. All auth logic/state
   (email, password, rememberMe, handleSubmit, useAuth) is unchanged. */

// Small inline brand marks for the SSO buttons (presentation only).
const MicrosoftIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l6-6C34.6 5.1 29.6 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.8 1.1 8 3l6-6C34.6 6.1 29.6 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.6l-6.5-5.5C29.6 34.7 27 35.6 24 35.6c-5.3 0-9.7-3.4-11.3-8.1l-6.6 5.1C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.5 5.5C41.4 35.6 44 30.2 44 24c0-1.2-.1-2.4-.4-3.5z" />
  </svg>
);

/* Remember-me control. Same behaviour as before (native checkbox carries
   all the state/keyboard focus/a11y), just visually replaced so the tick
   reads as the app's teal accent instead of the browser's default blue. */
const RememberMe = ({ checked, onChange, disabled, id }) => (
  <label
    htmlFor={id}
    className={`group flex select-none items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
  >
    <span className="relative inline-flex h-5 w-5 flex-shrink-0 items-center justify-center">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="peer absolute inset-0 h-full w-full cursor-[inherit] appearance-none rounded-[6px] border outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35"
        /* Driven directly off `checked` via inline style rather than Tailwind's
           checked:bg-[...]/checked:border-[...] variants: this checkbox sits
           inside <main> (App.jsx's PublicLayout), and odoo-global.css's
           `main .bg-white { background: #fff !important }` catch-all was
           matching this element's own `bg-white` class and permanently
           winning over the (non-!important) checked: utility classes, so the
           box never visually looked checked even though the click/state
           worked. Setting color directly here can't be out-cascaded. */
        style={{
          borderColor: checked ? 'var(--accent)' : 'var(--line-3)',
          background: checked ? 'var(--accent)' : '#fff'
        }}
      />
      <Check
        className="pointer-events-none relative h-3.5 w-3.5 scale-75 text-white opacity-0 transition-all duration-150 peer-checked:scale-100 peer-checked:opacity-100"
        strokeWidth={3.5}
      />
    </span>
    <span className="ml-2 text-[14px]" style={{ color: 'var(--body)' }}>Remember me</span>
  </label>
);

const Login = () => {
  const { login, rememberMe: storedRememberMe, rememberedEmail } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  // Restore the saved preference AND the saved email, so ticking the box
  // visibly does something the next time you land here.
  useEffect(() => {
    setRememberMe(storedRememberMe);
    if (storedRememberMe && rememberedEmail) setEmail(rememberedEmail);
  }, [storedRememberMe, rememberedEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // NOTE: the preference is no longer written here. AuthContext.login()
    // persists it only once the credentials are accepted -- writing it up
    // front was shuffling the previous session between localStorage and
    // sessionStorage even when this attempt went on to fail.
    const result = await login(email, password, rememberMe);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Shared field styling -- theme-token borders/focus ring instead of the
  // old hardcoded hex values.
  const fieldClass =
    'h-[42px] w-full rounded-lg border bg-white text-[15px] outline-none transition-all duration-150 placeholder:text-[#9aa7ba] focus:ring-[3px]';
  const fieldStyle = { borderColor: 'var(--line-3)' };

  return (
    <div
      className="flex min-h-screen w-full items-center justify-center px-4 py-10"
      style={{ background: 'var(--bg)' }}
    >
      <div className="w-full max-w-[400px]">
        {/* Brand */}
        <div className="mb-7 flex flex-col items-center text-center">
          <img
            src={logoGWPC}
            alt="Great Workplace Club"
            className="h-auto w-[190px] object-contain"
          />
          <p className="mt-3 text-[13px]" style={{ color: 'var(--muted)' }}>
            Employee Engagement &amp; Survey Platform
          </p>
        </div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="rounded-xl p-7"
          style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}
        >
          <div className="mb-5 text-center">
            <h2 className="text-[20px] font-semibold" style={{ color: 'var(--ink)' }}>Sign in to your account</h2>
            <p className="mt-1 text-[13px]" style={{ color: 'var(--muted)' }}>Welcome back! Please enter your credentials.</p>
          </div>

          {error && (
            <div className="mb-4 flex items-center rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="mr-2 h-5 w-5 flex-shrink-0" />{error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Email address</label>
              <div className="group relative">
                <Mail
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--faint)' }}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${fieldClass} pl-12 pr-4`}
                  style={{ ...fieldStyle, '--tw-ring-color': 'var(--accent)' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--line-3)'; }}
                  placeholder="Enter your email address"
                  autoComplete="username"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Password</label>
              <div className="group relative">
                <Lock
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--faint)' }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${fieldClass} pl-12 pr-12`}
                  style={{ ...fieldStyle, '--tw-ring-color': 'var(--accent)' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--line-3)'; }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--faint)' }}
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between py-1">
              <RememberMe
                id="remember-me"
                checked={rememberMe}
                onChange={setRememberMe}
                disabled={loading}
              />
              <Link to="/forgot-password" className="text-[12px] font-medium transition-colors" style={{ color: 'var(--accent)' }}>
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group flex h-[46px] w-full items-center justify-center gap-3 rounded-lg text-[15px] font-semibold text-white transition-all duration-150 disabled:pointer-events-none disabled:opacity-50"
              style={{ background: 'var(--accent)' }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = 'var(--accent-strong)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent)'; }}
            >
              {loading ? (
                <><div className="h-5 w-5 animate-spin rounded-full border-b-2 border-white" />Signing in...</>
              ) : (
                <>Sign in <span className="text-xl transition-transform duration-150 group-hover:translate-x-1">→</span></>
              )}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1" style={{ background: 'var(--line)' }} />
            <span className="text-[12px]" style={{ color: 'var(--faint)' }}>or</span>
            <div className="h-px flex-1" style={{ background: 'var(--line)' }} />
          </div>

          <div className="space-y-2">
            <button
              type="button"
              disabled
              className="flex h-[42px] w-full items-center justify-center gap-3 rounded-lg border text-[14px] font-medium disabled:cursor-not-allowed disabled:opacity-70"
              style={{ borderColor: 'var(--line-3)', color: 'var(--body)' }}
            >
              <MicrosoftIcon />Continue with Microsoft
            </button>
            <button
              type="button"
              disabled
              className="flex h-[42px] w-full items-center justify-center gap-3 rounded-lg border text-[14px] font-medium disabled:cursor-not-allowed disabled:opacity-70"
              style={{ borderColor: 'var(--line-3)', color: 'var(--body)' }}
            >
              <GoogleIcon />Continue with Google
            </button>
          </div>

          <div className="mt-5 text-center text-[13px]" style={{ color: 'var(--body)' }}>
            Don&apos;t have an account?{' '}
            <Link to="/contact" className="font-medium" style={{ color: 'var(--accent)' }}>Get Certified</Link>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center gap-3 text-[12px]" style={{ color: 'var(--faint)' }}>
          <Link to="/contact" className="hover:underline">Privacy Policy</Link>
          <span>|</span>
          <Link to="/contact" className="hover:underline">Terms of Service</Link>
          <span>|</span>
          <Link to="/contact" className="hover:underline">Help &amp; Support</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
